import { ProgressBar } from '@pixi/ui';
import {
	Easing, Group, Tween,
} from '@tweenjs/tween.js';
import { Container, Graphics } from 'pixi.js';

const RADIUS = 10;
const WIDTH = 100;
export const TIMER_BAR_HEIGHT = 20;
const BORDER_THICKNESS = 4;
const DEFAULT_BACKGROUND = 0;
const DEFAULT_FOREGROUND = 0xffffff;
// const HALF_WIDTH = WIDTH / 2;
// const HALF_HEIGHT = TIMER_BAR_HEIGHT / 2;

const X_START = 0;
const Y_START = 0;

const FILLED_INITIAL_WIDTH = (WIDTH - (BORDER_THICKNESS * 2));
const FILLED_HEIGHT = TIMER_BAR_HEIGHT - (BORDER_THICKNESS * 2);
const FILLED_X_START = X_START + BORDER_THICKNESS;
const FILLED_Y_START = Y_START + BORDER_THICKNESS;
const PROGRESS_EASING = Easing.Linear.None;

const FADE_TIME = 1000; //in MS so it can be quick
const FADE_EASING = Easing.Exponential.Out;

export class TimerBar extends Container {

	#initialValue = 0;
	currentValue = 0;
	/**
	 * @type {ProgressBar}
	 */
	bar = null;
	/** 
	 * @type {Tween}
	 */
	tween = null;
	/**
	 * @type {Group}
	 */
	group = new Group();

	/**
	 * @type {number}
	 */
	backgroundColor = DEFAULT_BACKGROUND;

	/**
	 * @type {number}
	 */
	foregroundColor = DEFAULT_FOREGROUND;

	constructor(debug = false) {
		super();

		const bg = new Graphics().beginFill(this.backgroundColor).drawRoundedRect(X_START, Y_START, WIDTH, TIMER_BAR_HEIGHT, RADIUS);
		const fill = new Graphics()
			.beginFill(this.backgroundColor)
			.drawRoundedRect(0, 0, WIDTH, TIMER_BAR_HEIGHT, RADIUS)
			.beginFill(this.foregroundColor)
			.drawRoundedRect(FILLED_X_START, FILLED_Y_START, FILLED_INITIAL_WIDTH, FILLED_HEIGHT, RADIUS);
		this.bar = new ProgressBar({
			bg,
			fill,
			progress: 100,
		});

		this.addChild(this.bar);
		this.visible = false;
		this.debug = debug;
	}

	/**
	 * @param {number | undefined} newValue
	 */
	set value(newValue) {
		if (newValue === this.#initialValue && this.tween?.isPlaying()) {
			return;
		}

		this.#initialValue = newValue;

		this.#setupTween();
	}

	get initialValue() {
		return this.#initialValue;
	}

	update() {
		const tweenUpdate = this.group.update() ?? false;
		const returnValue = tweenUpdate || (this.wasVisible && !this.#initialValue);
		if (this.debug) {
			console.log('in timer bar update, returning', returnValue, tweenUpdate, this.visible, !this.#initialValue);
		}

		if (this.wasVisible && !this.visible) {
			this.wasVisible = false;
		}

		return returnValue;
	}

	#setupTween() {
		this.group.removeAll();

		if (!this.#initialValue) {
			if (this.visible) {
				this.#fadeOut();
			}

			return;
		}

		this.visible = true;
		this.alpha = 0;

		this.currentValue = this.#initialValue;

		this.#startProgress();
		this.#fadeIn();

	}

	fadeIn() {
		this.#fadeIn();
	}

	fadeOut() {
		this.#fadeOut();
	}

	#fadeIn() {
		new Tween(this, this.group)
			.easing(FADE_EASING)
			.to({
				alpha: 1,
			}, FADE_TIME)
			.start();
	}

	#fadeOut() {
		new Tween(this, this.group)
			.easing(FADE_EASING)
			.to({
				alpha: 0,
			}, FADE_TIME)
			.onComplete(() => {
				this.visible = false;
			}).start();
	}

	#startProgress() {
		//the docs make it seem like you can reuse tweens, but it doesn't work properly if you reuse them
		this.tween = new Tween(this, this.group).easing(PROGRESS_EASING);
		this.tween.to({
			currentValue: 0,
		}, this.#initialValue * 1000).onUpdate((updatedObject) => {
			this.bar.progress = ((updatedObject?.currentValue ?? 0) / this.#initialValue) * 100;
		}).onComplete(() => {
			this.#initialValue = undefined;
			this.#fadeOut();
		}).start();
	}

}
